import type { RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HotelProperty } from "@/types";

interface HotelPropertySelectorProps {
  selectorRef: RefObject<HTMLDivElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedProperty: HotelProperty | null;
  activeProperties: HotelProperty[];
  hasMultipleHotelProperties: boolean;
  onSelectProperty: (propertyId: number) => void;
}

export function HotelPropertySelector({
  selectorRef,
  open,
  onOpenChange,
  selectedProperty,
  activeProperties,
  hasMultipleHotelProperties,
  onSelectProperty,
}: HotelPropertySelectorProps) {
  if (!selectedProperty) return null;

  return (
    <div className="relative" ref={selectorRef}>
      <button
        onClick={() => hasMultipleHotelProperties && onOpenChange(!open)}
        aria-label="Selecionar filial"
        className={cn(
          "flex items-center gap-2 px-3 h-11 rounded-xl",
          "bg-muted/30 border border-border/50",
          "text-sm transition-all duration-180 ease-[cubic-bezier(0.16,1,0.3,1)]",
          hasMultipleHotelProperties
            ? "hover:bg-muted/50 hover:border-primary/30 cursor-pointer"
            : "cursor-default",
        )}
      >
        <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
        <span className="max-w-36 truncate font-medium">
          {selectedProperty.name}
        </span>
        {hasMultipleHotelProperties ? (
          <ChevronDown
            className={cn(
              "w-3 h-3 text-muted-foreground shrink-0 transition-transform duration-180",
              open && "rotate-180",
            )}
          />
        ) : null}
      </button>

      <AnimatePresence>
        {open && hasMultipleHotelProperties ? (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute right-0 top-full mt-2 w-80 bg-card/85 backdrop-blur-xl border border-border/40 rounded-2xl shadow-2xl shadow-black/40 z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
              <span className="font-semibold text-sm">Selecionar Filial</span>
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full font-medium">
                {activeProperties.length} filiais
              </span>
            </div>
            <div className="divide-y divide-border/20 max-h-64 overflow-y-auto">
              {activeProperties.map((property) => (
                <button
                  key={property.id}
                  onClick={() => onSelectProperty(property.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/5 hover:translate-x-1 transition-all duration-180 ease-out text-left",
                    property.id === selectedProperty.id &&
                      "bg-primary/5 text-foreground",
                  )}
                >
                  <div
                    className={cn(
                      "p-2 rounded-xl shrink-0 transition-colors duration-180",
                      property.id === selectedProperty.id
                        ? "bg-emerald-500/20"
                        : "bg-muted/30",
                    )}
                  >
                    <MapPin
                      className={cn(
                        "w-4 h-4 transition-colors duration-180",
                        property.id === selectedProperty.id
                          ? "text-emerald-400"
                          : "text-muted-foreground",
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {property.name}
                    </p>
                    {property.city || property.state ? (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        {[property.city, property.state]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    ) : null}
                    {property.id === selectedProperty.id ? (
                      <span className="flex items-center gap-1.5 text-2.5 text-emerald-400 font-semibold uppercase tracking-wide mt-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Ativa
                      </span>
                    ) : null}
                  </div>
                  {property.id === selectedProperty.id ? (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : null}
                </button>
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
